import { useEffect, useRef } from 'react';
import { useDiscoveryStore } from '../stores/discoveryStore';
import { useGraphConsent } from './useGraphConsent';
import { startAnalysis, saveProfile } from '../services/discoveryApi';

const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** Decode the inline base64 report into a browser object URL for download.
 *  The backend returns the MIME type (DOCX, or PDF when rendering is enabled). */
function base64ToObjectUrl(base64: string, mimeType: string = DOCX_MIME): string {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: mimeType || DOCX_MIME }));
}

/** Shape of the JSON body the API returns for a 403 (consent) or 502 (failed). */
interface ApiErrorBody {
  status?: string;
  error?: string;
  consentUrl?: string | null;
}

/** Narrow an unknown axios error to the server's JSON error body. */
function readErrorBody(err: unknown): ApiErrorBody {
  // reason: axios errors are untyped here; narrow defensively to the JSON body.
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === 'object') {
    const d = data as ApiErrorBody;
    return {
      status: typeof d.status === 'string' ? d.status : undefined,
      error: typeof d.error === 'string' ? d.error : undefined,
      consentUrl: typeof d.consentUrl === 'string' ? d.consentUrl : null,
    };
  }
  return {};
}

/**
 * Main orchestration hook for the Discovery wizard.
 *
 * Generation is synchronous: a single start-analysis request runs the full
 * pipeline server-side and returns the finished report inline (no polling). This
 * is the reliable path on Linux Consumption — polling/downloading across scaled
 * instances was hitting in-memory state on a different worker (404 / hang).
 *
 * Graph consent is obtained at login time via extraScopesToConsent, so no popup
 * is needed here. After completion, auto-saves the profile for future pre-fill.
 */
export function useDiscovery() {
  const store = useDiscoveryStore();
  const { acquireApiToken } = useGraphConsent();
  const profileSavedRef = useRef(false);
  const startedRef = useRef(false);

  // Auto-start analysis when the flow mounts (consent already granted at login)
  useEffect(() => {
    if (startedRef.current) return;
    if (!store.selectedIndustry || !store.userContext) return;
    if (store.wizardStep !== 'analysis' || store.analysisId) return;
    if (store.status === 'completed' || store.status === 'generating_ai_content') return;

    startedRef.current = true;

    (async () => {
      const apiToken = await acquireApiToken();
      if (!apiToken) {
        store.updateProgress('failed', null, [], 0, '', 'Failed to acquire access token. Please sign in again.');
        return;
      }

      store.setApiToken(apiToken);
      store.beginGenerating();

      try {
        const response = await startAnalysis(
          store.selectedIndustry!,
          store.userContext!,
          apiToken,
          store.storeReport,
          store.scanMode,
          store.selectedSiteIds
        );

        if (response.status !== 'completed' || !response.reportBase64) {
          store.updateProgress(
            'failed', null, [], 0, '',
            response.error || "We couldn't generate your report. Please try again."
          );
          return;
        }

        store.completeAnalysis({
          analysisId: response.analysisId,
          fileName: response.fileName || 'Transformr-Readiness-Report.docx',
          score: response.score ?? null,
          tier: response.tier ?? null,
          aiContentUsed: response.aiContentUsed ?? false,
          reportStored: response.reportStored ?? false,
          reportObjectUrl: base64ToObjectUrl(response.reportBase64, response.reportMimeType),
        });
        profileSavedRef.current = false;
      } catch (err) {
        console.error('Failed to generate report:', err);
        const body = readErrorBody(err);
        if (body.status === 'consent_required' && body.consentUrl) {
          store.updateProgress(
            'failed', null, [], 0, '',
            'Admin consent is required before the scan can run.', body.consentUrl
          );
        } else {
          store.updateProgress(
            'failed', null, [], 0, '',
            body.error || "We couldn't generate your report. Please try again."
          );
        }
      }
    })();
  }, [store, acquireApiToken]);

  // Auto-save profile after successful analysis completion
  useEffect(() => {
    if (store.status !== 'completed' || profileSavedRef.current) return;
    if (!store.apiToken || !store.selectedIndustry || !store.userContext) return;

    profileSavedRef.current = true;

    const profileData: Record<string, string> = {
      industry: store.selectedIndustry,
      user_role: store.userContext.user_role,
      company_name: store.userContext.company_name,
      country: store.userContext.country,
      ai_goals: store.userContext.ai_goals,
    };
    if (store.userContext.custom_industry) {
      profileData.custom_industry = store.userContext.custom_industry;
    }
    if (store.userContext.website_url) {
      profileData.website_url = store.userContext.website_url;
    }

    saveProfile(profileData, store.apiToken).catch((err) =>
      console.error('Failed to save profile:', err)
    );
  }, [store.status, store.apiToken, store.selectedIndustry, store.userContext]);

  return {
    // State
    wizardStep: store.wizardStep,
    selectedIndustry: store.selectedIndustry,
    userContext: store.userContext,
    analysisId: store.analysisId,
    apiToken: store.apiToken,
    status: store.status,
    currentStep: store.currentStep,
    stepsCompleted: store.stepsCompleted,
    progressPct: store.progressPct,
    message: store.message,
    error: store.error,
    consentUrl: store.consentUrl,
    reportReady: store.reportReady,
    reportStored: store.reportStored,
    fileName: store.fileName,
    score: store.score,
    tier: store.tier,
    aiContentUsed: store.aiContentUsed,

    // Actions
    reset: store.reset,
  };
}
